import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { LogOut, RefreshCw } from "lucide-react";

export default function AdminDashboard() {
  const [user, setUser] = useState(null);
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const [{ data: me }, { data: list }] = await Promise.all([
        api.get("/auth/me"),
        api.get("/enquiries"),
      ]);
      setUser(me);
      setEnquiries(list);
    } catch {
      navigate("/admin");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    navigate("/admin");
  };

  const markRead = async (id) => {
    try {
      await api.patch(`/enquiries/${id}/read`);
      setEnquiries((list) => list.map((e) => (e.id === id ? { ...e, read: true } : e)));
    } catch { /* ignore */ }
  };

  if (loading) {
    return (
      <div data-testid="admin-loading" className="min-h-screen bg-surface flex items-center justify-center font-mono text-xs uppercase tracking-[0.25em] text-slate-500">
        Loading dashboard...
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-surface">
      <header className="bg-white border-b border-ink/10 px-6 md:px-12 h-16 flex items-center justify-between">
        <div className="flex items-center gap-6">
          <span className="font-display font-semibold text-lg tracking-tight text-ink">Enquiries Dashboard</span>
          <Link data-testid="dashboard-back-home" to="/" className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 hover:text-chrome transition-colors duration-300">← Site</Link>
        </div>
        <div className="flex items-center gap-4">
          <span data-testid="dashboard-user" className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 hidden sm:block">{user?.email}</span>
          <button data-testid="dashboard-refresh-button" onClick={load} className="w-10 h-10 border border-ink/15 flex items-center justify-center text-ink hover:bg-ink hover:text-paper transition-colors duration-300" aria-label="Refresh">
            <RefreshCw size={15} />
          </button>
          <button data-testid="admin-logout-button" onClick={logout} className="flex items-center gap-2 bg-ink text-paper font-mono text-[10px] uppercase tracking-[0.2em] px-4 py-3 hover:bg-chrome transition-colors duration-300">
            <LogOut size={13} /> Logout
          </button>
        </div>
      </header>
      <main className="px-6 md:px-12 py-10">
        <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-chrome mb-6" data-testid="enquiries-count">
          {enquiries.length} enquir{enquiries.length === 1 ? "y" : "ies"} received
        </p>
        {enquiries.length === 0 ? (
          <div data-testid="enquiries-empty" className="bg-white border border-ink/10 p-16 text-center text-slate-500">
            No enquiries yet. New submissions from the contact form will appear here.
          </div>
        ) : (
          <div className="bg-white border border-ink/10 overflow-x-auto">
            <table data-testid="enquiries-table" className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-ink/10">
                  {["Date", "Name", "Company", "Email", "Phone", "Tonnage", "Message", ""].map((h) => (
                    <th key={h} className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 px-5 py-4">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {enquiries.map((e) => (
                  <tr key={e.id} data-testid={`enquiry-row-${e.id}`} className={`border-b border-ink/5 align-top ${e.read ? "opacity-60" : ""}`}>
                    <td className="px-5 py-4 whitespace-nowrap text-slate-500">{new Date(e.created_at).toLocaleDateString("en-ZA", { day: "2-digit", month: "short", year: "numeric" })}</td>
                    <td className="px-5 py-4 font-semibold text-ink">{e.name}</td>
                    <td className="px-5 py-4 text-slate-600">{e.company || "—"}</td>
                    <td className="px-5 py-4"><a href={`mailto:${e.email}`} className="text-chrome hover:underline">{e.email}</a></td>
                    <td className="px-5 py-4 text-slate-600">{e.phone || "—"}</td>
                    <td className="px-5 py-4 text-slate-600">{e.tonnage || "—"}</td>
                    <td className="px-5 py-4 text-slate-600 max-w-xs">{e.message}</td>
                    <td className="px-5 py-4">
                      {!e.read && (
                        <button data-testid={`enquiry-read-${e.id}`} onClick={() => markRead(e.id)} className="font-mono text-[10px] uppercase tracking-[0.2em] text-chrome hover:text-ink transition-colors duration-300 whitespace-nowrap">
                          Mark read
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
